export const PIPELINE_STATUSES = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled'
};

export const STEP_STATUSES = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed'
};

export const PIPELINE_STEPS = {
    FILE_PROCESSING: 'file_processing',
    SEARCH_TERMS: 'search_terms',
    BIBLIOGRAPHY: 'bibliography',
    CODE_DISCOVERY: 'code_discovery',
    CODE_MARKING: 'code_marking',
    ARTICLE_GENERATION: 'article_generation'
};

export const STEP_DETAILS = {
    [PIPELINE_STEPS.FILE_PROCESSING]: {
        name: 'File Processing',
        icon: '📁',
        description: 'Processing uploaded files and extracting content'
    },
    [PIPELINE_STEPS.SEARCH_TERMS]: {
        name: 'Search Terms Generation',
        icon: '🔍',
        description: 'Generating search terms from analyzed content'
    },
    [PIPELINE_STEPS.BIBLIOGRAPHY]: {
        name: 'Bibliography Search',
        icon: '📚',
        description: 'Searching academic databases for relevant articles'
    },
    [PIPELINE_STEPS.CODE_DISCOVERY]: {
        name: 'Code Discovery',
        icon: '🧠',
        description: 'Discovering themes and patterns in the content'
    },
    [PIPELINE_STEPS.CODE_MARKING]: {
        name: 'Code Marking',
        icon: '🏷️',
        description: 'Applying discovered codes to the content'
    },
    [PIPELINE_STEPS.ARTICLE_GENERATION]: {
        name: 'Article Generation',
        icon: '✍️',
        description: 'Generating comprehensive research article'
    }
};

export const SUPPORTED_FILE_TYPES = {
    AUDIO: ['mp3', 'wav', 'm4a'],
    DOCUMENT: ['pdf']
};

export const FILE_VALIDATION = {
    MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
    MAX_FILES: 20,
    ALLOWED_EXTENSIONS: [...SUPPORTED_FILE_TYPES.AUDIO, ...SUPPORTED_FILE_TYPES.DOCUMENT]
};

export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const validateFile = (file) => {
    const errors = [];
    
    // Check file size
    if (file.size > FILE_VALIDATION.MAX_FILE_SIZE) {
        errors.push(`File size exceeds ${formatFileSize(FILE_VALIDATION.MAX_FILE_SIZE)} limit`);
    }
    
    // Check file extension
    const extension = file.name.split('.').pop().toLowerCase();
    if (!FILE_VALIDATION.ALLOWED_EXTENSIONS.includes(extension)) {
        errors.push(`File type .${extension} is not supported. Allowed types: ${FILE_VALIDATION.ALLOWED_EXTENSIONS.join(', ')}`);
    }
    
    return errors;
};

export const validateFileList = (files) => {
    const errors = [];
    
    // Check number of files
    if (files.length === 0) {
        errors.push('At least one file is required');
    }
    
    if (files.length > FILE_VALIDATION.MAX_FILES) {
        errors.push(`Maximum ${FILE_VALIDATION.MAX_FILES} files allowed`);
    }
    
    // Validate each file
    files.forEach((file, index) => {
        const fileErrors = validateFile(file);
        fileErrors.forEach(error => {
            errors.push(`File ${index + 1} (${file.name}): ${error}`);
        });
    });
    
    return errors;
};

export const getStatusColor = (status) => {
    switch (status) {
        case PIPELINE_STATUSES.PENDING:
        case STEP_STATUSES.PENDING:
            return 'text-gray-500 bg-gray-100';
        case PIPELINE_STATUSES.PROCESSING:
        case STEP_STATUSES.PROCESSING:
            return 'text-amber-500 bg-amber-100';
        case PIPELINE_STATUSES.COMPLETED:
        case STEP_STATUSES.COMPLETED:
            return 'text-green-600 bg-green-100';
        case PIPELINE_STATUSES.FAILED:
        case STEP_STATUSES.FAILED:
            return 'text-red-600 bg-red-100';
        case PIPELINE_STATUSES.CANCELLED:
            return 'text-orange-600 bg-orange-100';
        default:
            return 'text-gray-500 bg-gray-100';
    }
};

export const calculateProgress = (steps) => {
    if (!steps || steps.length === 0) return 0;
    
    const completedSteps = steps.filter(step => step.status === STEP_STATUSES.COMPLETED).length;
    return Math.round((completedSteps / steps.length) * 100);
};

export const getCurrentStep = (steps) => {
    if (!steps || steps.length === 0) return null;
    
    // Find the first step that's not completed
    const currentStep = steps.find(step => step.status !== STEP_STATUSES.COMPLETED);
    return currentStep || steps[steps.length - 1]; // Return last step if all completed
};

export const getNextStep = (steps, currentStepName) => {
    if (!steps || steps.length === 0) return null;
    
    const currentIndex = steps.findIndex(step => step.step_name === currentStepName);
    if (currentIndex === -1 || currentIndex === steps.length - 1) return null;
    
    return steps[currentIndex + 1];
};

export const canRetryStep = (step) => {
    return step && 
           step.status === STEP_STATUSES.FAILED && 
           step.retry_count < step.max_retries;
};

export const canCancelPipeline = (pipeline) => {
    return pipeline && 
           [PIPELINE_STATUSES.PENDING, PIPELINE_STATUSES.PROCESSING].includes(pipeline.status);
};

export const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const estimateCompletionTime = (pipeline) => {
    if (!pipeline || !pipeline.steps) return null;
    
    const avgStepTime = 2; // minutes per step (rough estimate)
    const remainingSteps = pipeline.steps.filter(step => 
        step.status === STEP_STATUSES.PENDING || step.status === STEP_STATUSES.PROCESSING
    ).length;
    
    if (remainingSteps === 0) return null;
    
    const estimatedMinutes = remainingSteps * avgStepTime;
    const now = new Date();
    const completionTime = new Date(now.getTime() + estimatedMinutes * 60000);
    
    return completionTime;
};